import React, {useEffect, useMemo, useRef, useState} from 'react';
import styled from 'styled-components';
import {IEventCalendar} from '../../../../../store/reducers/eventCalendarReducer/eventCalendarReducerTypes';
import {fetchInitialAirportForSelect} from '../../../../../utils/helpers/selectDataFetchers/fetchAirports';
import {DebounceSelectOptionProps} from '../../../../../components/UI/AntCustom/DebounceSelect';
import {defineMessages, useIntl} from 'react-intl';

interface IEventCalendarTimelineProps {
  events: IEventCalendar[];
  onEventClick?: (event: IEventCalendar) => void;
  currentYear?: number;
}

interface TimelineEvent {
  event: IEventCalendar;
  startMonth: number; // 0-11
  endMonth: number; // 0-11
  startDay: number; // день начала в месяце
  endDay: number; // день окончания в месяце
  eventDays: number; // количество дней, которые занимает событие
  row: number; // строка для размещения
  airportNames: string[];
}

const TimelineContainer = styled.div`
  border: 1px solid #ECEDF0;
  border-radius: 8px;
  overflow-x: auto;
  overflow-y: auto;
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  max-height: 600px; /* Максимальная высота для появления вертикального скролла */
`;

const TimelineHeader = styled.div`
  border-bottom: 1px solid #ECEDF0;
  height: 30px;
  display: flex;
  align-items: center;
  background: #FBFBFC;
  min-width: fit-content;
`;

const MonthHeader = styled.div`
  flex: 1 0 0;
  border-right: 1px solid #ECEDF0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 10px;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 18px;
  color: #8F929C;
  text-align: center;
  
  &:last-child {
    border-right: none;
  }

  @media (max-width: 768px) {
    flex: 0 0 auto;
    min-width: 80px;
    width: 80px;
  }
`;

const TimelineBody = styled.div`
  position: relative;
  min-height: 400px;
  display: flex;
  flex-direction: column;
  flex: 0 0 auto; /* Убираем flex: 1, чтобы контент мог расширяться */
  min-width: fit-content;
`;

const TimelineRow = styled.div`
  position: relative;
  display: flex;
  flex: 0 0 54px; /* Фиксированная высота строки, соответствует высоте события (54px) */
  min-height: 54px;
  
  &:last-child {
    border-bottom: none;
  }
`;

const MonthCell = styled.div`
  flex: 1 0 0;
  border-right: 1px solid #ECEDF0;
  background: #FFFFFF;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 49px;
  align-items: flex-end;
  justify-content: flex-end;
  position: relative;
  
  &:last-child {
    border-right: none;
  }

  @media (max-width: 768px) {
    flex: 0 0 auto;
    min-width: 80px;
    width: 80px;
  }
`;

const EventBlock = styled.div<{color: string; left: number; width: number; row: number}>`
  position: absolute;
  left: ${props => props.left}px;
  width: ${props => props.width}px;
  top: ${props => props.row * 54 + 12}px;
  background: ${props => {
    // Преобразуем hex в rgba с прозрачностью 0.1
    const hex = props.color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    return `rgba(${r}, ${g}, ${b}, 0.1)`;
  }};
  border-left: 3px solid ${props => props.color};
  border-top: none;
  border-right: none;
  border-bottom: none;
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0;
  z-index: 1;
  box-sizing: border-box;
  white-space: nowrap;
  
  &:hover {
    opacity: 0.8;
  }
`;

const EventName = styled.div`
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 18px;
  color: #000000;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
`;

const EventLocation = styled.div`
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 10px;
  line-height: 16px;
  color: grey;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
`;

const MonthNameMessages = defineMessages({
  january: {
    id: "eventCalendarTimeline.month.january",
    defaultMessage: "Jan",
  },
  february: {
    id: "eventCalendarTimeline.month.february",
    defaultMessage: "Feb",
  },
  march: {
    id: "eventCalendarTimeline.month.march",
    defaultMessage: "Mar",
  },
  april: {
    id: "eventCalendarTimeline.month.april",
    defaultMessage: "Apr",
  },
  may: {
    id: "eventCalendarTimeline.month.may",
    defaultMessage: "May",
  },
  june: {
    id: "eventCalendarTimeline.month.june",
    defaultMessage: "Jun",
  },
  july: {
    id: "eventCalendarTimeline.month.july",
    defaultMessage: "Jul",
  },
  august: {
    id: "eventCalendarTimeline.month.august",
    defaultMessage: "Aug",
  },
  september: {
    id: "eventCalendarTimeline.month.september",
    defaultMessage: "Sep",
  },
  october: {
    id: "eventCalendarTimeline.month.october",
    defaultMessage: "Oct",
  },
  november: {
    id: "eventCalendarTimeline.month.november",
    defaultMessage: "Nov",
  },
  december: {
    id: "eventCalendarTimeline.month.december",
    defaultMessage: "Dec",
  },
});

export const EventCalendarTimeline: React.FC<IEventCalendarTimelineProps> = ({
  events,
  onEventClick,
  currentYear = new Date().getFullYear(),
}) => {
  const intl = useIntl();
  
  const monthNames = useMemo(() => [
    intl.formatMessage(MonthNameMessages.january),
    intl.formatMessage(MonthNameMessages.february),
    intl.formatMessage(MonthNameMessages.march),
    intl.formatMessage(MonthNameMessages.april),
    intl.formatMessage(MonthNameMessages.may),
    intl.formatMessage(MonthNameMessages.june),
    intl.formatMessage(MonthNameMessages.july),
    intl.formatMessage(MonthNameMessages.august),
    intl.formatMessage(MonthNameMessages.september),
    intl.formatMessage(MonthNameMessages.october),
    intl.formatMessage(MonthNameMessages.november),
    intl.formatMessage(MonthNameMessages.december),
  ], [intl]);
  const [airportCache, setAirportCache] = useState<Record<number, string>>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(1200);

  // Отслеживаем ширину контейнера
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth();
    const resizeObserver = new ResizeObserver(updateWidth);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Загружаем названия аэропортов
  useEffect(() => {
    const loadAirports = async () => {
      const airportIds = new Set<number>();
      events.forEach(event => {
        event.blocks?.forEach(block => {
          if (block.airportIds && block.airportIds.length > 0) {
            block.airportIds.forEach(id => airportIds.add(id));
          }
        });
      });

      const cache: Record<number, string> = {};
      
      // Тестовые названия аэропортов для демонстрации
      const testAirportNames: Record<number, string> = {
        1: 'Мадрид',
        2: 'Барселона',
        3: 'Париж',
        4: 'Лондон',
        5: 'Рим',
      };

      await Promise.all(
        Array.from(airportIds).map(async (airportId) => {
          // Используем тестовые данные, если они есть
          if (testAirportNames[airportId]) {
            cache[airportId] = testAirportNames[airportId];
            return;
          }

          try {
            const result = await fetchInitialAirportForSelect(airportId);
            if (result && result.length > 0) {
              // Извлекаем название из label (формат: "ICAO, IATA, Name")
              const label = result[0].label;
              const parts = label.split(', ');
              cache[airportId] = parts[parts.length - 1] || label; // Берем последнюю часть (название)
            }
          } catch (e) {
            console.error(`Failed to load airport ${airportId}:`, e);
            // Используем тестовое название, если загрузка не удалась
            if (testAirportNames[airportId]) {
              cache[airportId] = testAirportNames[airportId];
            }
          }
        })
      );
      setAirportCache(cache);
    };

    if (events.length > 0) {
      loadAirports();
    }
  }, [events]);

  // Вычисляем позиции событий
  const timelineEvents = useMemo(() => {
    const processedEvents: TimelineEvent[] = [];
    const rowOccupancy: Record<number, Array<{start: number; end: number}>> = {}; // row -> [{start, end}]

    events.forEach(event => {
      // Парсим даты как локальное время, без смещения по часовому поясу
      const parseDate = (dateString: string): Date => {
        const dateStr = String(dateString).trim();
        
        // Проверяем формат "YYYY-MM-DD HH:mm:ss"
        const dateTimeMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})$/);
        if (dateTimeMatch) {
          const [, year, month, day, hour, minute, second] = dateTimeMatch.map(Number);
          // Создаем Date в локальном времени с указанными компонентами
          return new Date(year, month - 1, day, hour, minute, second);
        }
        
        // Проверяем формат "YYYY-MM-DD"
        const dateMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (dateMatch) {
          const [, year, month, day] = dateMatch.map(Number);
          return new Date(year, month - 1, day, 0, 0, 0);
        }
        
        // Fallback: пытаемся парсить как есть
        return new Date(dateString);
      };
      
      const startDate = parseDate(event.dateFrom);
      const endDate = parseDate(event.dateTo);
      
      // Проверяем, попадает ли событие в текущий год
      if (startDate.getFullYear() > currentYear || endDate.getFullYear() < currentYear) {
        return;
      }

      const startMonth = startDate.getFullYear() === currentYear ? startDate.getMonth() : 0;
      const endMonth = endDate.getFullYear() === currentYear ? endDate.getMonth() : 11;
      const startDay = startDate.getFullYear() === currentYear ? startDate.getDate() : 1;
      const endDay = endDate.getFullYear() === currentYear ? endDate.getDate() : new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0).getDate();
      
      // Вычисляем количество дней события
      // Для события 28.11 12:00 - 30.11 12:00 это будет 2 дня (28, 29, 30 - но считаем от начала 28 до начала 30)
      // Используем разницу в днях между датами начала и окончания
      const startDateOnly = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
      const endDateOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
      // Для события 28-30 ноября: endDateOnly - startDateOnly = 2 дня
      const diffTime = endDateOnly.getTime() - startDateOnly.getTime();
      const eventDays = diffTime / (1000 * 60 * 60 * 24); // Разница в днях
      
      // Если eventDays = 0 (событие в один день), устанавливаем минимум 1 день
      const finalEventDays = eventDays === 0 ? 1 : eventDays;

      // Получаем названия аэропортов
      const airportNames: string[] = [];
      event.blocks?.forEach(block => {
        if (block.airportIds && block.airportIds.length > 0) {
          block.airportIds.forEach(airportId => {
            const name = airportCache[airportId];
            if (name && !airportNames.includes(name)) {
              airportNames.push(name);
            }
          });
        }
      });

      // Находим подходящую строку
      let row = 0;
      while (true) {
        if (!rowOccupancy[row]) {
          rowOccupancy[row] = [];
        }

        // Проверяем пересечения
        const hasOverlap = rowOccupancy[row].some(occupied => {
          return !(endMonth < occupied.start || startMonth > occupied.end);
        });

        if (!hasOverlap) {
          rowOccupancy[row].push({start: startMonth, end: endMonth});
          break;
        }
        row++;
      }

      processedEvents.push({
        event,
        startMonth,
        endMonth,
        startDay,
        endDay,
        eventDays: finalEventDays,
        row,
        airportNames,
      });
    });

    return processedEvents;
  }, [events, currentYear, airportCache]);

  // Вычисляем ширину месяца (в пикселях)
  const monthWidth = useMemo(() => {
    return containerWidth / 12;
  }, [containerWidth]);

  // Вычисляем позицию и ширину события
  // Ширина события рассчитывается относительно количества дней, которые оно занимает
  const getEventPosition = (event: TimelineEvent) => {
    // Если событие в одном месяце
    if (event.startMonth === event.endMonth) {
      const monthLeft = event.startMonth * monthWidth;
      const daysInMonth = new Date(currentYear, event.startMonth + 1, 0).getDate();
      
      // Позиция начала: левая граница месяца + прогресс от начала месяца
      // Например, для 28 ноября: (28 - 1) / 30 ≈ 0.9 (90% от начала месяца)
      const startDayProgress = (event.startDay - 1) / daysInMonth;
      const left = monthLeft + startDayProgress * monthWidth;
      
      // Ширина: количество дней события / количество дней в месяце * ширина месяца
      // Например, для события 28-30 ноября (2 дня): (2 / 30) * monthWidth
      // Формула: ширина одного дня = monthWidth / daysInMonth
      // Ширина события = ширина одного дня × eventDays
      const width = (event.eventDays / daysInMonth) * monthWidth;
      
      return {left, width};
    } else {
      // Событие растягивается на несколько месяцев
      const startMonthLeft = event.startMonth * monthWidth;
      const daysInStartMonth = new Date(currentYear, event.startMonth + 1, 0).getDate();
      const startDayProgress = (event.startDay - 1) / daysInStartMonth;
      const left = startMonthLeft + startDayProgress * monthWidth;

      const endMonthLeft = event.endMonth * monthWidth;
      const daysInEndMonth = new Date(currentYear, event.endMonth + 1, 0).getDate();
      // Для позиции окончания используем (endDay - 1), так как endDay - это день окончания
      // Например, для 30 ноября: (30 - 1) / 30 ≈ 0.967 (96.7% от начала месяца)
      const endDayProgress = (event.endDay - 1) / daysInEndMonth;
      const endPosition = endMonthLeft + endDayProgress * monthWidth;

      const width = endPosition - left;
      return {left, width};
    }
  };

  const handleEventClick = (event: IEventCalendar) => {
    if (onEventClick) {
      onEventClick(event);
    }
  };

  return (
    <TimelineContainer ref={containerRef}>
      <TimelineHeader>
        {monthNames.map((month, index) => (
          <MonthHeader key={index}>{month}</MonthHeader>
        ))}
      </TimelineHeader>
      <TimelineBody>
        {/* Сетка месяцев */}
        {Array.from({length: Math.max(timelineEvents.length > 0 ? Math.max(...timelineEvents.map(e => e.row)) + 1 : 1, 1)}).map((_, rowIndex) => (
          <TimelineRow key={rowIndex}>
            {monthNames.map((_, monthIndex) => (
              <MonthCell key={monthIndex} />
            ))}
          </TimelineRow>
        ))}
        
        {/* События */}
        {timelineEvents.map((timelineEvent, index) => {
          const {left, width} = getEventPosition(timelineEvent);
          const eventColor = timelineEvent.event.color || '#1890FF';
          const airportName = timelineEvent.airportNames.length > 0 
            ? timelineEvent.airportNames.join(', ') 
            : '';
          
          return (
            <EventBlock
              key={`${timelineEvent.event.eventCalendarId}-${index}`}
              color={eventColor}
              left={left}
              width={width}
              row={timelineEvent.row}
              onClick={() => handleEventClick(timelineEvent.event)}
            >
              <EventName>{timelineEvent.event.eventName}</EventName>
              {airportName && <EventLocation>{airportName}</EventLocation>}
            </EventBlock>
          );
        })}
      </TimelineBody>
    </TimelineContainer>
  );
};

